import type { MatchAnchors, MatchState } from '../types';
import { assertNever } from './machine';

/**
 * SES-4, SES-8, SES-10, INV-7, INV-10 — the single temporal authority.
 *
 * Two pure functions, both total, both closed by `assertNever`:
 *
 *   `stateAt(anchors, t)`            — which state was the match in at wall-clock time `t`?
 *   `deriveMatchMinute(anchors, s, t)` — the one derivation SES-10 and INV-10 name.
 *
 * **Nothing else in the app may compute a match minute.** INV-7 makes `matchMinute` the primary
 * temporal key and `createdAt` metadata; the only sanctioned use of `createdAt` here is SES-8's
 * boundary re-derivation, where it is an *input to* the derivation rather than a record key.
 *
 * **Totality is the point (INV-10).** The return type excludes `null` and `undefined`; every
 * branch is clamped non-negative; every non-finite input degrades to a defined value rather than
 * producing `NaN`. A `matchMinute` of `NaN` would satisfy a naive "not null, not negative" check
 * and then poison every downstream comparison, so it is closed off at the helpers.
 *
 * **The minute is derived at creation (SES-10, revision 2).** For an audio observation, creation is
 * the moment recording STARTS. That ordering is the recorder's contract (design §8.2), not this
 * module's — but it is why the signature takes `t` rather than reading a clock: the caller is the
 * only one who knows which instant counts.
 */

/**
 * A unit conversion, deliberately NOT in `config/`.
 *
 * NFR-9 governs "defaults, thresholds, caps" — things a person may tune. The length of a minute is
 * not one of them, and putting it in the tunable module would invite exactly the edit that breaks
 * SES-4.
 */
const MS_PER_MINUTE = 60_000;

/** True when `anchor` is a usable timestamp and `t` is at or after it. */
function atOrAfter(anchor: number | null, t: number): boolean {
  return anchor !== null && Number.isFinite(anchor) && Number.isFinite(t) && t >= anchor;
}

/**
 * Whole minutes elapsed since `anchor`, floored and clamped at zero.
 *
 * Returns `0` when the anchor is missing or either value is non-finite. A missing anchor is not
 * reachable for the state that reads it — `firstHalf` implies `kickoffTs` was written by the same
 * transaction that set the state — but INV-10 says "no reachable state permits an undefined
 * minute", and a defined `0` is the answer that keeps that true if the row is ever corrupt.
 */
function elapsedMinutes(anchor: number | null, t: number): number {
  if (anchor === null || !Number.isFinite(anchor) || !Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((t - anchor) / MS_PER_MINUTE));
}

/** SES-5's half length, normalised to a non-negative whole number of minutes. */
function halfLength(anchors: MatchAnchors): number {
  const { halfLengthMin } = anchors;
  if (!Number.isFinite(halfLengthMin)) return 0;
  return Math.max(0, Math.floor(halfLengthMin));
}

/**
 * The state the match was in at `t`, ignoring `fullTimeTs`.
 *
 * Split out because `finalMinute` must ask "what was happening when Full Time was tapped?", and
 * asking `stateAt` at exactly `fullTimeTs` would answer `'fullTime'` and recurse forever. The
 * anchors partition the wall-clock line, so reading them newest-first recovers the state.
 */
function stateBeforeFullTime(anchors: MatchAnchors, t: number): MatchState {
  if (atOrAfter(anchors.kickoff2Ts, t)) return 'secondHalf';
  if (atOrAfter(anchors.halfTs, t)) return 'halftime';
  if (atOrAfter(anchors.kickoffTs, t)) return 'firstHalf';
  return 'preMatch';
}

/**
 * SES-8 — which state was the match in at wall-clock time `t`, given its anchors?
 *
 * Returns only the five states that occupy wall-clock time. `triage`, `distill` and `archived` are
 * post-`fullTime` lifecycle steps with no anchor of their own, and they derive the same minute as
 * `fullTime` does, so re-derivation lands on the identical value either way.
 */
export function stateAt(anchors: MatchAnchors, t: number): MatchState {
  if (atOrAfter(anchors.fullTimeTs, t)) return 'fullTime';
  return stateBeforeFullTime(anchors, t);
}

/**
 * The minute a finished match rests at — design §2.4's `finalMinute`.
 *
 * Total even for a match abandoned at halftime with `kickoff2Ts` null, because SES-6 requires
 * Abandon to write `fullTimeTs`. If `fullTimeTs` is somehow absent the latest surviving anchor is
 * used, so INV-10 still holds against a corrupt row instead of returning `NaN`.
 */
export function finalMinute(anchors: MatchAnchors): number {
  const stopped = anchors.fullTimeTs;
  if (stopped !== null && Number.isFinite(stopped)) {
    return deriveMatchMinute(anchors, stateBeforeFullTime(anchors, stopped), stopped);
  }
  const fallback = latestAnchor(anchors);
  if (fallback === null) return 0;
  return deriveMatchMinute(anchors, stateBeforeFullTime(anchors, fallback), fallback);
}

function latestAnchor(anchors: MatchAnchors): number | null {
  const candidates = [anchors.kickoff2Ts, anchors.halfTs, anchors.kickoffTs].filter(
    (v): v is number => v !== null && Number.isFinite(v),
  );
  if (candidates.length === 0) return null;
  return Math.max(...candidates);
}

/**
 * SES-10 / INV-10 — the single pure function that derives `matchMinute`.
 *
 * | State | Minute |
 * |---|---|
 * | `idle`, `preMatch` | `0` |
 * | `firstHalf` | `floor((t − kickoffTs)/60000)`, clamped ≥ 0 (SES-4) |
 * | `halftime` | `halfLengthMin` |
 * | `secondHalf` | `halfLengthMin + floor((t − kickoff2Ts)/60000)`, clamped ≥ `halfLengthMin` (SES-4) |
 * | `fullTime`, `triage`, `distill`, `archived` | `finalMinute(anchors)` |
 *
 * INV-10 scopes totality to the states in which an Observation can be created; `idle` and
 * `archived` are given defined values anyway so the `switch` is exhaustive over the whole enum and
 * `assertNever` can close it. Adding a tenth `MatchState` breaks this build.
 */
export function deriveMatchMinute(anchors: MatchAnchors, state: MatchState, t: number): number {
  switch (state) {
    case 'idle':
    case 'preMatch':
      return 0;
    case 'firstHalf':
      return elapsedMinutes(anchors.kickoffTs, t);
    case 'halftime':
      return halfLength(anchors);
    case 'secondHalf':
      return halfLength(anchors) + elapsedMinutes(anchors.kickoff2Ts, t);
    case 'fullTime':
    case 'triage':
    case 'distill':
    case 'archived':
      return finalMinute(anchors);
    default:
      return assertNever(state);
  }
}

/**
 * SES-8 — re-derive one Observation's or MatchNote's minute after the half boundaries move.
 *
 * `Observation` does not store the state it was created in, and it does not need to: the new
 * anchors partition the wall-clock line, so `stateAt(newAnchors, createdAt)` recovers it. This uses
 * `createdAt` as an **input to derivation**, not as a temporal key, so INV-7 is intact — and it
 * routes through the same `deriveMatchMinute` that created the value in the first place, which is
 * what SES-8's "re-derive through the single pure function" requires.
 */
export function rederiveMatchMinute(anchors: MatchAnchors, createdAt: number): number {
  return deriveMatchMinute(anchors, stateAt(anchors, createdAt), createdAt);
}
